import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, RotateCcw, Clock, BookOpen, Award, CheckCircle2, Mail, Lock, FileText, Video, Sparkles } from 'lucide-react';
import { getUser } from '../lib/api';
import { isLiveLike } from '../lib/course-types';

function LiveSessionBadge({ liveDateUtc }) {
  if (!liveDateUtc) return null;
  const now = new Date();
  const session = new Date(liveDateUtc);
  const diffMs = session - now;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffMs < -2 * 60 * 60 * 1000) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
        Session ended
      </span>
    );
  }

  if (diffHours >= -2 && diffHours <= 2) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full animate-pulse">
        <span className="w-1.5 h-1.5 bg-red-500 rounded-full" /> Live now
      </span>
    );
  }

  if (diffHours > 0 && diffHours <= 24) {
    const time = session.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
        Live today at {time}
      </span>
    );
  }

  if (diffDays > 0 && diffDays <= 7) {
    const day = session.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    const time = session.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return (
      <span className="inline-flex items-center text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
        Live {day} at {time}
      </span>
    );
  }

  if (diffDays > 7) {
    const date = session.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <span className="inline-flex items-center text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
        Live session {date}
      </span>
    );
  }

  return null;
}

function CourseCard({ enrollment, locked }) {
  const e = enrollment;
  const isCompleted = e.status === 'completed';
  const isNotStarted = e.status === 'not_started';
  const isLive = isLiveLike(e.course_type);
  const playUrl = isLive ? `/courses/${e.course_slug}/live` : `/courses/${e.course_slug}/play`;

  const buttonLabel = isCompleted ? 'Review' : isNotStarted ? 'Start course' : 'Continue';
  const ButtonIcon = locked ? Lock : isCompleted ? RotateCcw : Play;

  return (
    <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col sm:flex-row transition-shadow ${locked ? 'opacity-90' : 'hover:shadow-md'}`}>
      {/* Hero image / placeholder */}
      <div className="block sm:w-56 shrink-0 relative">
        {e.hero_image_url ? (
          <img
            src={e.hero_image_url}
            alt={e.course_title}
            className="w-full h-36 sm:h-full object-cover"
          />
        ) : (
          <div className="w-full h-36 sm:h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <BookOpen size={32} className="text-slate-300" />
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 bg-slate-900/20 flex items-center justify-center">
            <Lock size={24} className="text-white drop-shadow" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {e.course_type === 'hybrid' ? 'Hybrid event' : isLive ? 'Live course' : 'Self-paced'}
            </span>
            {isLive && <LiveSessionBadge liveDateUtc={e.live_date_utc} />}
            {isCompleted && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                <CheckCircle2 size={11} /> Completed
              </span>
            )}
          </div>

          <h3 className="text-lg font-bold text-slate-900 leading-snug">
            {e.course_title}
          </h3>

          {e.short_description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{e.short_description}</p>
          )}

          {/* Meta chips */}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
            {e.duration_minutes && (
              <span className="inline-flex items-center gap-1">
                <Clock size={12} /> {e.duration_minutes >= 60 ? `${Math.round(e.duration_minutes / 60)}h` : `${e.duration_minutes}m`}
              </span>
            )}
            {e.score != null && (
              <span className="inline-flex items-center gap-1">
                <Award size={12} /> Score: {e.score}%
              </span>
            )}
          </div>
        </div>

        {/* Bottom row: progress + button */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
          {!isLive && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>{isCompleted ? 'Complete' : `${e.progress_percent}% complete`}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isCompleted ? 'bg-emerald-500' : 'bg-brand'}`}
                  style={{ width: `${isCompleted ? 100 : e.progress_percent}%` }}
                />
              </div>
            </div>
          )}
          {isLive && <div className="flex-1" />}

          {locked ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg shrink-0 bg-slate-100 text-slate-400 cursor-not-allowed">
              <Lock size={14} /> Set up account to start
            </span>
          ) : (
            <Link
              to={playUrl}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg shrink-0 transition ${
                isCompleted
                  ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  : 'bg-brand text-white hover:bg-brand-dark'
              }`}
            >
              <ButtonIcon size={14} />
              {buttonLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function DripCard({ item }) {
  const TypeIcon = { scorm: BookOpen, video: Video, pdf: FileText }[item.content_type] || BookOpen;
  const typeLabel = { scorm: 'Interactive module', video: 'Video', pdf: 'PDF Document' }[item.content_type] || 'Content';
  const isViewed = !!item.viewed_at;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col sm:flex-row hover:shadow-md transition-shadow">
      <div className="block sm:w-56 shrink-0 relative">
        {item.hero_image_url ? (
          <img src={item.hero_image_url} alt={item.course_title} className="w-full h-36 sm:h-full object-cover" />
        ) : (
          <div className="w-full h-36 sm:h-full bg-gradient-to-br from-brand-light to-orange-100 flex items-center justify-center">
            <Sparkles size={32} className="text-brand/40" />
          </div>
        )}
        <span className="absolute top-2 left-2 bg-brand text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
          Follow-up
        </span>
      </div>

      <div className="flex-1 p-5 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-brand/70">
              <TypeIcon size={11} /> {typeLabel}
            </span>
            {isViewed && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                <CheckCircle2 size={11} /> Viewed
              </span>
            )}
          </div>

          <h3 className="text-lg font-bold text-slate-900 leading-snug">{item.title}</h3>
          <p className="text-sm text-slate-500 mt-1">From: {item.course_title}</p>
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
          <div className="flex-1" />
          <Link
            to={`/drip/${item.access_id}`}
            className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg shrink-0 transition ${
              isViewed
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-brand text-white hover:bg-brand-dark'
            }`}
          >
            <Play size={14} />
            {isViewed ? 'Review' : 'Start'}
          </Link>
        </div>
      </div>
    </div>
  );
}

function SetupBanner({ email }) {
  return (
    <div className="bg-brand/5 border border-brand/20 rounded-xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <div className="w-11 h-11 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
        <Mail size={20} className="text-brand" />
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="font-bold text-slate-900">Finish setting up your account</h2>
        <p className="text-sm text-slate-600 mt-0.5">
          We sent a setup link to <strong>{email}</strong>. Set your password to unlock your courses.
        </p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [enrollments, setEnrollments] = useState([]);
  const [dripItems, setDripItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUserState] = useState(getUser);
  const [isProvisional, setIsProvisional] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getUser()) return navigate('/login');
    const opts = { credentials: 'include' };

    Promise.all([
      fetch('/api/me', opts).then((r) => {
        if (r.status === 401) { navigate('/login'); return null; }
        return r.json();
      }),
      fetch('/api/me/enrollments', opts).then((r) => {
        if (r.status === 401) return null;
        return r.json();
      }),
      fetch('/api/me/drip-content', opts).then((r) => {
        if (!r.ok) return { drip_items: [] };
        return r.json();
      }),
    ]).then(([meData, enrollData, dripData]) => {
      if (!meData || !enrollData) return;
      setUserState(meData.user);
      setIsProvisional(!meData.user.has_password);
      setEnrollments(enrollData.enrollments || []);
      setDripItems(dripData?.drip_items || []);
    }).finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return <p className="max-w-5xl mx-auto px-4 py-10 text-slate-500">Loading…</p>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      {isProvisional && <SetupBanner email={user?.email} />}

      <h1 className="text-3xl font-bold mb-8">My courses</h1>
      {enrollments.length === 0 && dripItems.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 text-lg mb-2">No courses yet</p>
          <Link to="/library" className="text-brand font-semibold hover:underline">
            Browse the course library
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {enrollments.map((e) => (
            <CourseCard key={e.id} enrollment={e} locked={isProvisional} />
          ))}
        </div>
      )}

      {dripItems.length > 0 && (
        <>
          <h2 className="text-xl font-bold mt-12 mb-4 flex items-center gap-2">
            <Sparkles size={18} className="text-brand" /> Follow-up materials
          </h2>
          <div className="space-y-4">
            {dripItems.map((item) => (
              <DripCard key={item.access_id} item={item} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
